import * as AgGrid from "ag-grid-community";
import * as gridUtils from "grid/utils";
import * as datasource from "grid/datasource";
import { GridController } from "grid/grid_controller";
import { DEFAULT_COL_DEF, ACTIONS_COL_DEF, TEXT_FILTER_COL_DEF } from "../../grid/columns";

interface Seller {
  id: string;
  name: string;
  refund_auto_approval_label?: string;
  remove_url?: string;
  edit_url?: string;
  actions?: string;
}

const RELOAD_EVENT = "sellers:reload";

export default class extends GridController<Seller> {
  private boundReload = () => this.reload();

  connect() {
    super.connect();
    this.element.addEventListener(RELOAD_EVENT, this.boundReload);
  }

  disconnect() {
    this.element.removeEventListener(RELOAD_EVENT, this.boundReload);
    super.disconnect();
  }

  reload() {
    this.gridApi?.purgeInfiniteCache();
  }

  openCreateModal() {
    const url = this.gridTarget.dataset.newUrl;
    const turboFrame = document.querySelector("turbo-frame#modal") as HTMLElement & { src?: string };
    if (turboFrame && url) {
      turboFrame.src = url;
    }
  }

  override buildGrid() {
    const { i18nJson, paginationPageSize, confirmDelete } = this.gridTarget.dataset;
    const i18n = JSON.parse(i18nJson || "{}");

    const colDefs: AgGrid.ColDef[] = [
      { field: "id", ...DEFAULT_COL_DEF, sortable: false },
      { field: "name", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: false },
      {
        field: "refund_auto_approval_label",
        colId: "refund_auto_approval",
        headerName: i18n["refund_auto_approval"] || "Auto-approval",
        ...DEFAULT_COL_DEF,
        sortable: false,
      },
      {
        ...ACTIONS_COL_DEF,
        cellClass: "actions-cell",
        onCellClicked: (params) => {
          const target = params.event.target as HTMLElement;
          const btn = target?.tagName?.toLowerCase() === "button" ? target : target?.closest("button");
          const action = btn?.dataset?.action;
          if (!action) return;

          if (action === "edit") {
            const turboFrame = document.querySelector("turbo-frame#modal") as HTMLElement & { src?: string };
            if (turboFrame && params.node.data.edit_url) {
              turboFrame.src = params.node.data.edit_url;
            }
            return;
          }

          if (action !== "remove") return;

          const msgTemplate = confirmDelete || "Delete seller?";
          const msg = msgTemplate.includes("%{name}")
            ? msgTemplate.replace("%{name}", params.node.data.name || "")
            : msgTemplate;
          if (!confirm(msg)) return;

          fetch(params.node.data.remove_url, {
            method: "DELETE",
            headers: {
              "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
            },
          }).then(() => {
            gridApi.purgeInfiniteCache();
          });
        },
      },
    ];

    const { maxBlocksInCache, onCellFocused, paginationAutoPageSize, ...defaultOptions } =
      gridUtils.DEFAULT_GRID_OPTIONS;
    const dataSource = new datasource.Datasource({ callbacks: {}, limit: paginationPageSize });
    const pageSize = parseInt(paginationPageSize) || 20;

    const options: AgGrid.GridOptions<Seller> = {
      ...defaultOptions,
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<Seller, any>),
      },
      cacheBlockSize: pageSize,
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n }),
      pagination: true,
      paginationPageSize: pageSize,
      paginationPageSizeSelector: false,
      paginationAutoPageSize: false,
      domLayout: "autoHeight",
      datasource: dataSource,
      onFilterChanged: (params) => {
        if (params.api.getGridOption("rowModelType") == "infinite") {
          params.api.paginationGoToPage(0);
          params.api.purgeInfiniteCache();
        }
      },
    };

    const gridApi = AgGrid.createGrid(this.gridTarget, { ...options });
    dataSource.gridApi = gridApi;
    return gridApi;
  }
}
